import { onBeforeUnmount, ref } from 'vue'

export type AutosaveStatus = 'idle' | 'saving' | 'saved' | 'error'

/**
 * Per-section autosave with an inline status. Generalizes the watch + 800ms
 * debounce + "Saving…/Saved" pattern the standalone settings pages used
 * (e.g. CrmSettingsPage, EmailSettingsPage) so every drawer section persists
 * on change rather than behind a global Save button.
 *
 * Usage:
 *   const { status, errorMessage, schedule, saveNow, retry } = useAutosave(doSave)
 *   // free-text inputs → schedule() (debounced); toggles/selects/Done → saveNow()
 *
 * `saveFn` should be a fresh read-modify-write where it shares a document with
 * another writer (ownership_settings, /settings/email) so concurrent edits are
 * not clobbered.
 */
export function useAutosave(saveFn: () => Promise<void>, opts: { delay?: number } = {}) {
  const delay = opts.delay ?? 800
  const status = ref<AutosaveStatus>('idle')
  const errorMessage = ref('')
  let timer: ReturnType<typeof setTimeout> | null = null
  let savedTimer: ReturnType<typeof setTimeout> | null = null
  let inFlight: Promise<void> | null = null
  let rerun = false

  function clearTimer() {
    if (timer) { clearTimeout(timer); timer = null }
  }

  async function saveOnce() {
    status.value = 'saving'
    errorMessage.value = ''
    try {
      await saveFn()
      status.value = 'saved'
      if (savedTimer) clearTimeout(savedTimer)
      savedTimer = setTimeout(() => { if (status.value === 'saved') status.value = 'idle' }, 2000)
    } catch (e: unknown) {
      status.value = 'error'
      errorMessage.value = (e as Error)?.message || 'Could not save'
    }
  }

  // One save at a time: a request made mid-save runs once more afterwards, so the
  // latest state always lands last.
  function run(): Promise<void> {
    clearTimer()
    if (inFlight) {
      rerun = true
      return inFlight
    }
    inFlight = (async () => {
      do {
        rerun = false
        await saveOnce()
      } while (rerun)
    })().finally(() => { inFlight = null })
    return inFlight
  }

  /** Debounced save — for free-text inputs. */
  function schedule() {
    clearTimer()
    timer = setTimeout(() => { timer = null; void run() }, delay)
  }

  /** Immediate save — for toggles, selects, and the field-picker "Done". */
  function saveNow() {
    return run()
  }

  onBeforeUnmount(clearTimer)

  return { status, errorMessage, schedule, saveNow, retry: saveNow }
}
